// Search index derived from the master NAV_TREE. Editing nav-tree.ts is
// enough — anything commented there is automatically excluded from search.

import { NAV_TREE, NavNode } from './nav-tree';

export interface PageEntry {
  label: string;
  path: string;
  module: string;
  roles?: string[];
  kind: 'page' | 'tab' | 'subtab';
  requiresImpersonation?: boolean;
}

function flatten(
  nodes: NavNode[],
  breadcrumb: string,
  inheritedRoles: string[] | undefined,
  inheritedRequiresImpersonation: boolean,
  out: PageEntry[],
): void {
  for (const node of nodes) {
    const roles = node.roles && node.roles.length ? node.roles : inheritedRoles;
    const requiresImpersonation = inheritedRequiresImpersonation || node.requiresImpersonation === true;
    const kind = node.kind ?? 'page';

    // 'group' nodes contribute to the breadcrumb but are NOT searchable —
    // they exist purely to organize the hierarchy (e.g. "Staff Management").
    if (kind !== 'group' && node.path) {
      out.push({
        label: node.label,
        path: node.path,
        module: breadcrumb || 'Modules',
        roles,
        kind,
        requiresImpersonation: requiresImpersonation || undefined,
      });
    }

    if (node.children?.length) {
      const nextBreadcrumb = breadcrumb ? `${breadcrumb} > ${node.label}` : node.label;
      flatten(node.children, nextBreadcrumb, roles, requiresImpersonation, out);
    }
  }
}

function buildRegistry(): PageEntry[] {
  const entries: PageEntry[] = [];
  flatten(NAV_TREE, '', undefined, false, entries);
  // Dedupe by `path + label` so identical entries from nested branches collapse.
  const seen = new Set<string>();
  return entries.filter((e) => {
    const key = `${e.path}::${e.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export const PAGE_REGISTRY: PageEntry[] = buildRegistry();
